/**
 * Keep the stories feed's filters across a reload.
 *
 * Reloading dropped everything back to All languages / Last 24 Hours and hid
 * the Coverage control, so a tester who refreshed mid-pass silently changed the
 * query they believed they were running.
 *
 * Only the APPLIED filter is stored — written when a search is submitted, not
 * as controls are touched — so a half-edited filter nobody ran is never
 * restored as though it had been. Same reasoning as the suggestion scope.
 *
 * localStorage, not sessionStorage. The first implementation used
 * sessionStorage on the reasoning that a filter should survive a refresh
 * without outliving the browser session. That reasoning did not survive
 * contact: a browser or automation harness that treats a reload as a new
 * session clears sessionStorage, so the filter was reported lost on reload in
 * exactly the environment QA runs in, and the behaviour depended on which
 * browser you used. "Survives a reload" has to mean it, so the storage has to
 * be the durable one.
 *
 * What stops it surprising someone later is Reset All, which clears the stored
 * filter as well as the screen — so "reset" still means reset on the next load,
 * and that is now the only thing that clears it.
 *
 * Reading falls back to the old sessionStorage key once, so a tab that already
 * has a filter stored there does not lose it on the deploy that moves this.
 */

const KEY = 'alchemiq.storiesFeedFilters.v1';

/** The fields that define the query. Deliberately not the whole slice. */
const FIELDS = [
  'selectedLanguages',
  'selectedCountries',
  'excludeLanguages',
  'excludeCountries',
  'selectedCategories',
  'selectedTimeRange',
  'countryCoverageMode',
  'languageCoverageMode',
];

export function loadPersistedFeedFilters() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY) ?? window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Only known fields, so a stale shape from an older build cannot inject
    // arbitrary state into the slice.
    return FIELDS.reduce((acc, f) => (parsed[f] !== undefined ? { ...acc, [f]: parsed[f] } : acc), {});
  } catch (_e) {
    return null;
  }
}

export function persistFeedFilters(state) {
  if (typeof window === 'undefined' || !state) return;
  try {
    const slim = FIELDS.reduce((acc, f) => (state[f] !== undefined ? { ...acc, [f]: state[f] } : acc), {});
    window.localStorage.setItem(KEY, JSON.stringify(slim));
  } catch (_e) { /* private mode, quota — never block a search over this */ }
}

export function clearPersistedFeedFilters() {
  if (typeof window === 'undefined') return;
  // Both, so Reset All also clears a filter left behind by the old key.
  try { window.localStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
  try { window.sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
}

export const __FIELDS = FIELDS;
